import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Search,
  Filter,
  RefreshCw,
  Clock,
  User,
  Activity,
  Shield,
  ChevronLeft,
  ChevronRight,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Authentication/security and administration categories; every other category counts as Data Events.
const NON_DATA_CATEGORIES = ["auth", "security", "admin", "user", "invitation", "invite", "resend_credentials", "bcm_manager", "demo"];

export default function AuditLogsPage() {
  const { user, api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    action: "",
    resourceType: "",
    days: 30,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("logs");

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        days: filters.days.toString(),
      });
      if (filters.action) params.append("action", filters.action);
      if (filters.resourceType) params.append("resource_type", filters.resourceType);

      const response = await api.get(`/audit/logs?${params}`);
      setLogs(response.data.logs || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0,
      }));
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  }, [api, pagination.page, pagination.limit, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get(`/audit/logs/summary?days=${filters.days}`);
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch audit summary:", error);
    }
  }, [api, filters.days]);

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [fetchLogs, fetchSummary]);

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/audit/logs/export?format=${format}&days=${filters.days}`);
      if (format === "csv") {
        const blob = new Blob([response.data.csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data.logs, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.json`;
        a.click();
      }
      toast.success(`Audit logs exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export audit logs");
    }
  };

  const getActionIcon = (action) => {
    if (action?.startsWith("auth")) return <User className="h-4 w-4 text-blue-500" />;
    if (action?.startsWith("bia") || action?.startsWith("risk"))
      return <Activity className="h-4 w-4 text-sky-700" />;
    if (action?.startsWith("plan")) return <FileText className="h-4 w-4 text-green-500" />;
    if (action?.startsWith("policy") || action?.startsWith("training"))
      return <Shield className="h-4 w-4 text-purple-500" />;
    return <Clock className="h-4 w-4 text-gray-500" />;
  };

  const getActionBadgeColor = (action) => {
    if (action?.includes("delete")) return "bg-red-100 text-red-800";
    if (action?.includes("create")) return "bg-green-100 text-green-800";
    if (action?.includes("update") || action?.includes("change"))
      return "bg-yellow-100 text-yellow-800";
    if (action?.includes("login")) return "bg-blue-100 text-blue-800";
    return "bg-gray-100 text-gray-800";
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredLogs = logs.filter((log) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.action?.toLowerCase().includes(query) ||
      log.user_email?.toLowerCase().includes(query) ||
      log.resource_type?.toLowerCase().includes(query)
    );
  });

  // Check if user has admin access
  if (user?.role !== "bcm_manager" && user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="audit-logs-access-denied">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to view audit logs. This feature is only available to
              BCM Managers and Platform Admins.
            </p>
            <Button onClick={() => (window.location.href = "/dashboard")} data-testid="return-dashboard-btn">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="audit-logs-title">Audit Logs</h1>
          <p className="text-gray-600">Track and monitor all user actions across the platform</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fetchLogs()} data-testid="refresh-logs-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Select onValueChange={(value) => handleExport(value)}>
            <SelectTrigger className="w-[140px]" data-testid="export-select">
              <Download className="h-4 w-4 mr-2" />
              Export
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">Export CSV</SelectItem>
              <SelectItem value="json">Export JSON</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="total-events-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Events</p>
                  <p className="text-2xl font-bold">{summary.total_events}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="auth-events-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Auth Events</p>
                  <p className="text-2xl font-bold">{summary.by_category?.auth || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="data-events-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <FileText className="h-5 w-5 text-sky-700" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Events</p>
                  <p className="text-2xl font-bold">
                    {Object.entries(summary.by_category || {}).filter(([cat]) => !NON_DATA_CATEGORIES.includes(cat)).reduce((sum, [, n]) => sum + (n || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="active-users-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Users</p>
                  <p className="text-2xl font-bold">{summary.top_users?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === "logs" ? "default" : "ghost"}
          onClick={() => setActiveTab("logs")}
          data-testid="logs-tab"
        >
          <FileText className="h-4 w-4 mr-2" />
          Logs
        </Button>
        <Button
          variant={activeTab === "activity" ? "default" : "ghost"}
          onClick={() => setActiveTab("activity")}
          data-testid="activity-tab"
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Activity
        </Button>
      </div>

      {activeTab === "logs" && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search logs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-logs-input"
                    />
                  </div>
                </div>
                <Select
                  value={filters.action || "all"}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, action: value === "all" ? "" : value }))}
                >
                  <SelectTrigger className="w-[180px]" data-testid="action-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    {filters.action || "All Actions"}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="auth">Authentication</SelectItem>
                    <SelectItem value="bia">BIA</SelectItem>
                    <SelectItem value="risk">Risk</SelectItem>
                    <SelectItem value="plan">Plans</SelectItem>
                    <SelectItem value="policy">Policies</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.days.toString()}
                  onValueChange={(value) =>
                    setFilters((prev) => ({ ...prev, days: parseInt(value) }))
                  }
                >
                  <SelectTrigger className="w-[150px]" data-testid="days-filter">
                    <Calendar className="h-4 w-4 mr-2" />
                    Last {filters.days} days
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64" data-testid="loading-indicator">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500" data-testid="no-logs">
                  <FileText className="h-12 w-12 mb-4 text-gray-300" />
                  <p>No audit logs found</p>
                  <p className="text-sm">Actions will be logged as users interact with the system</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="logs-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Timestamp
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          User
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Action
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Resource
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50" data-testid={`log-row-${log.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {formatTimestamp(log.timestamp || log.created_at)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-900">
                                {log.user_name || log.user_email || "Unknown"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getActionIcon(log.action)}
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeColor(
                                  log.action
                                )}`}
                              >
                                {log.action}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {log.resource_type}
                            {log.resource_id && (
                              <span className="text-gray-400 ml-1">
                                ({log.resource_id.slice(0, 8)}...)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                            {log.details && Object.keys(log.details).length > 0
                              ? JSON.stringify(log.details)
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {!loading && filteredLogs.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="pagination">
                  <div className="text-sm text-gray-500">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                    {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === "activity" && summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity by Category */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Activity by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(summary.by_category || {}).map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="text-sm capitalize">{category}</span>
                    </div>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Active Users */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(summary.top_users || []).map((user, index) => (
                  <div key={user.user_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm">{user.email}</span>
                    </div>
                    <span className="text-sm font-medium">{user.action_count} actions</span>
                  </div>
                ))}
                {(!summary.top_users || summary.top_users.length === 0) && (
                  <p className="text-sm text-gray-500">No user activity recorded yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Daily Activity Chart Placeholder */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Daily Activity (Last {filters.days} Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-end gap-1">
                {(summary.daily_activity || []).slice(-30).map((day, index) => (
                  <div
                    key={day.date}
                    className="flex-1 bg-blue-500 rounded-t min-h-[4px]"
                    style={{
                      height: `${Math.max(4, (day.count / Math.max(...(summary.daily_activity || []).map((d) => d.count), 1)) * 100)}%`,
                    }}
                    title={`${day.date}: ${day.count} events`}
                  />
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>
                  {summary.daily_activity?.[0]?.date || ""}
                </span>
                <span>
                  {summary.daily_activity?.[summary.daily_activity.length - 1]?.date || ""}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
